import { ArrowLeft, Check, Circle, Copy, LoaderCircle, LockKeyhole } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReasonSegmentSaveState } from '../store';
import type { ReasonCode, ReasonOption, ReasonTask } from '../types';
import {
  canonicalizeReasonNote,
  countCompletedSegments,
  countUnicodeCodePoints,
  effectiveReasonMediaDuration,
  getPreviewUrl,
  isReasonTaskLocked,
  isSegmentComplete,
  OTHER_REASON_CODE,
  shouldUseCommittedSegmentState,
  truncateUnicodeCodePoints,
} from '../types';
import { ReasonSegmentPlayer, type ReasonSegmentPlayerHandle } from './ReasonSegmentPlayer';
import { SegmentNavigator } from './SegmentNavigator';

interface ReasonEditorProps {
  task: ReasonTask;
  segmentIndex: number;
  saving: boolean;
  pendingSaveCount?: number;
  segmentSaveStates?: Record<number, ReasonSegmentSaveState>;
  committing: boolean;
  commitStage?: 'idle' | 'saving' | 'committing' | 'claiming' | 'committed';
  options?: ReasonOption[];
  nowMs?: number;
  observedMediaDurationS?: number | null;
  onBack: () => void;
  onSegmentChange: (index: number) => void;
  onReasonChange?: (segmentIndex: number, reason: ReasonCode) => void;
  onReasonsChange?: (segmentIndex: number, reasons: ReasonCode[]) => void;
  onReasonNoteChange?: (segmentIndex: number, reasonNote: string) => void;
  onMediaDuration?: (durationS: number) => void;
  onCommit: () => void;
}

function formatDeadline(value: number | null): string {
  if (value == null) return 'Starts after first commit';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);
}

function displayReasonLabel(option: ReasonOption): string {
  if (option.code === 'self_adjustment_over_8s') {
    return '3s以上调整镜头/调整自身状态等非工作状态的事件（摸脸、甩手等）';
  }
  return option.label;
}

function formatOutputDuration(value: number): string {
  const totalSeconds = Math.max(0, Math.round(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`
    : `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return true;
  return (
    target instanceof HTMLInputElement &&
    [
      'text',
      'search',
      'email',
      'url',
      'tel',
      'password',
      'number',
      'date',
      'datetime-local',
      'month',
      'time',
      'week',
    ].includes(target.type)
  );
}

export function ReasonEditor({
  task,
  segmentIndex,
  saving,
  pendingSaveCount = 0,
  segmentSaveStates = {},
  committing,
  commitStage = 'idle',
  options = [],
  nowMs = Date.now(),
  observedMediaDurationS = null,
  onBack,
  onSegmentChange,
  onReasonChange = () => undefined,
  onReasonsChange,
  onReasonNoteChange = () => undefined,
  onMediaDuration = () => undefined,
  onCommit,
}: ReasonEditorProps) {
  const effectiveMediaDuration = effectiveReasonMediaDuration(task, observedMediaDurationS);
  const segments = useMemo(
    () =>
      task.segments.filter(
        (candidate) =>
          Math.min(candidate.end_time_s, effectiveMediaDuration) >
          Math.min(candidate.start_time_s, effectiveMediaDuration)
      ),
    [effectiveMediaDuration, task.segments]
  );
  const segment = segments[segmentIndex];
  const playerRef = useRef<ReasonSegmentPlayerHandle>(null);
  const copyTimerRef = useRef<number | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const outputDurationS = segments.reduce(
    (total, candidate) =>
      total +
      Math.max(
        0,
        Math.min(candidate.end_time_s, effectiveMediaDuration) -
          Math.min(candidate.start_time_s, effectiveMediaDuration)
      ),
    0
  );
  const selectableCodes = useMemo(
    () =>
      new Set(
        options
          .filter((option) => option.enabled && option.selectable && option.taxonomy_version === 2)
          .map((option) => option.code)
      ),
    [options]
  );
  const useCommittedFields = shouldUseCommittedSegmentState(task, pendingSaveCount > 0);
  const completed = countCompletedSegments(segments, selectableCodes, useCommittedFields);
  const failedSaveCount = Object.values(segmentSaveStates).filter(
    (state) => state === 'failed'
  ).length;
  const complete =
    segments.length > 0 &&
    segments.every((candidate) =>
      isSegmentComplete(candidate, selectableCodes, useCommittedFields)
    );
  const nextIncompleteIndex = (() => {
    if (segments.length === 0) return null;
    for (let offset = 1; offset <= segments.length; offset += 1) {
      const candidateIndex = (segmentIndex + offset) % segments.length;
      const candidate = segments[candidateIndex];
      if (candidate && !isSegmentComplete(candidate, selectableCodes, useCommittedFields)) {
        return candidateIndex;
      }
    }
    return null;
  })();
  const locked = isReasonTaskLocked(task, nowMs);
  const committedReasonCodes =
    segment?.committed_reason_codes && segment.committed_reason_codes.length > 0
      ? segment.committed_reason_codes
      : segment?.committed_reason_code
        ? [segment.committed_reason_code]
        : segment?.reason_codes && segment.reason_codes.length > 0
          ? segment.reason_codes
          : segment?.reason_code
            ? [segment.reason_code]
            : [];
  const committedReasonNotes =
    segment?.committed_reason_notes && Object.keys(segment.committed_reason_notes).length > 0
      ? segment.committed_reason_notes
      : segment?.committed_reason_note
        ? { other: segment.committed_reason_note }
        : segment?.reason_notes && Object.keys(segment.reason_notes).length > 0
          ? segment.reason_notes
          : segment?.reason_note
            ? { other: segment.reason_note }
            : {};
  const selectedReasonCodes = useCommittedFields
    ? committedReasonCodes
    : (segment?.reason_codes ?? (segment?.reason_code ? [segment.reason_code] : []));
  const selectedReasonNotes = useCommittedFields
    ? committedReasonNotes
    : (segment?.reason_notes ?? (segment?.reason_note ? { other: segment.reason_note } : {}));
  const selectedReasonNote = selectedReasonNotes[OTHER_REASON_CODE] ?? '';
  const canonicalNote = canonicalizeReasonNote(selectedReasonNote);
  const noteLength = countUnicodeCodePoints(canonicalNote);
  const currentOtherIncomplete =
    selectedReasonCodes.includes(OTHER_REASON_CODE) &&
    segment != null &&
    !isSegmentComplete(segment, selectableCodes, useCommittedFields);
  const visibleOptions = options.filter(
    (option) =>
      (option.enabled && option.selectable && option.taxonomy_version === 2) ||
      selectedReasonCodes.includes(option.code)
  );
  const optionGroups = [...new Set(visibleOptions.map((option) => option.category_code))]
    .map((categoryCode) => {
      const categoryOptions = visibleOptions
        .filter((option) => option.category_code === categoryCode)
        .sort((left, right) => left.sort_order - right.sort_order);
      return {
        code: categoryCode,
        label:
          categoryOptions[0]?.category_label ??
          (categoryCode === 'legacy' ? '历史标签' : categoryCode),
        sort_order: categoryOptions[0]?.category_sort_order ?? 999,
        options: categoryOptions,
      };
    })
    .sort((left, right) => left.sort_order - right.sort_order);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing || isTextEditingTarget(event.target)) return;
      if (
        event.key === 'Enter' &&
        event.target instanceof HTMLInputElement &&
        event.target.dataset.reasonCheckbox === 'true'
      ) {
        event.preventDefault();
        event.target.click();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (segmentIndex > 0) void onSegmentChange(segmentIndex - 1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (segmentIndex < segments.length - 1) {
          void onSegmentChange(segmentIndex + 1);
        }
        return;
      }
      if (event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        playerRef.current?.togglePlayback();
        return;
      }
      if (event.key.toLowerCase() === 'n' && nextIncompleteIndex != null) {
        event.preventDefault();
        void onSegmentChange(nextIncompleteIndex);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [nextIncompleteIndex, onSegmentChange, segmentIndex, segments.length]);

  useEffect(() => {
    if (segments.length > 0 && segmentIndex >= segments.length) {
      void onSegmentChange(segments.length - 1);
    }
  }, [onSegmentChange, segmentIndex, segments.length]);

  useEffect(
    () => () => {
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
    },
    []
  );

  if (!segment) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-8 text-brand-text-primary sm:px-6">
        <button type="button" onClick={onBack} className="text-sm text-brand-text-secondary">
          Back to My Tasks
        </button>
        <div className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
          This task has no fail segments.
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-[1500px] px-4 py-6 sm:px-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-brand-border text-brand-text-secondary hover:text-brand-text-primary"
            aria-label="Back to My Tasks"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate font-mono text-base font-semibold text-brand-text-primary">
                {task.episode_id}
              </h1>
              <button
                type="button"
                onClick={() => {
                  void (async () => {
                    try {
                      if (!navigator.clipboard?.writeText) {
                        throw new Error('Clipboard API is unavailable');
                      }
                      await navigator.clipboard.writeText(task.episode_id);
                      setCopyState('copied');
                    } catch {
                      setCopyState('failed');
                    }
                    if (copyTimerRef.current != null) {
                      window.clearTimeout(copyTimerRef.current);
                    }
                    copyTimerRef.current = window.setTimeout(() => setCopyState('idle'), 2_000);
                  })();
                }}
                aria-label="Copy Episode ID"
                title="Copy Episode ID"
                className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-brand-border px-2 text-xs text-brand-text-secondary hover:text-brand-text-primary"
              >
                {copyState === 'copied' ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                <span>
                  {copyState === 'copied'
                    ? 'Copied'
                    : copyState === 'failed'
                      ? 'Copy failed'
                      : 'Copy ID'}
                </span>
              </button>
            </div>
            <p className="mt-0.5 text-xs text-brand-text-secondary">
              Revision {task.revision} · {completed} of {segments.length} segments complete · Fail
              duration {formatOutputDuration(outputDurationS)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-brand-text-secondary">
          {locked ? <LockKeyhole className="h-4 w-4 text-amber-400" /> : null}
          {locked ? 'Read-only' : `Editable until ${formatDeadline(task.edit_until_ms)}`}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="min-w-0">
          <ReasonSegmentPlayer
            ref={playerRef}
            src={getPreviewUrl(task)}
            segment={segment}
            segmentIndex={segmentIndex}
            segmentCount={segments.length}
            onMediaDuration={onMediaDuration}
            onPrevious={() => void onSegmentChange(segmentIndex - 1)}
            onNext={() => void onSegmentChange(segmentIndex + 1)}
          />
          <SegmentNavigator
            segments={segments}
            currentIndex={segmentIndex}
            selectableCodes={selectableCodes}
            useCommittedFields={useCommittedFields}
            saveStates={segmentSaveStates}
            onNavigate={onSegmentChange}
          />
        </div>

        <aside className="self-start rounded-lg border border-brand-border bg-brand-surface p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-brand-text-primary">Fail reason</h2>
            {saving ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-brand-text-secondary">
                <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> Saving draft
              </span>
            ) : failedSaveCount > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 text-xs text-red-300"
                title="Draft save paused. Your selections are kept locally."
              >
                <Circle className="h-3.5 w-3.5" /> Save failed
              </span>
            ) : pendingSaveCount > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-300">
                <Circle className="h-3.5 w-3.5" /> Queued
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
                <Check className="h-3.5 w-3.5" /> Saved
              </span>
            )}
          </div>

          <p className="mt-5 text-xs font-medium text-brand-text-secondary">
            Segment {segmentIndex + 1}
          </p>
          {selectedReasonCodes.length > 0 ? (
            <fieldset className="mt-2 flex flex-wrap gap-1.5" aria-label="Selected reasons">
              {selectedReasonCodes.map((reasonCode) => (
                <span
                  key={reasonCode}
                  className="rounded-sm border border-brand-orange/60 bg-brand-orange/10 px-2 py-1 text-[11px] text-brand-text-primary"
                >
                  {displayReasonLabel(
                    options.find((option) => option.code === reasonCode) ?? {
                      code: reasonCode,
                      category_code: 'legacy',
                      label: reasonCode,
                      enabled: false,
                      selectable: false,
                      sort_order: 999,
                      taxonomy_version: 1,
                    }
                  )}
                </span>
              ))}
            </fieldset>
          ) : null}
          <div className="mt-3 max-h-[52vh] space-y-5 overflow-y-auto pr-1">
            {optionGroups.map((group) => (
              <fieldset key={group.code} disabled={locked || committing}>
                <legend className="text-xs font-semibold text-brand-text-secondary">
                  {group.label}
                </legend>
                <div className="mt-2 space-y-1.5">
                  {group.options.map((option) => {
                    const disabled =
                      !option.enabled || !option.selectable || option.taxonomy_version !== 2;
                    const checked = selectedReasonCodes.includes(option.code);
                    return (
                      <label
                        key={option.code}
                        className={[
                          'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors',
                          checked
                            ? 'border-brand-orange bg-brand-orange/10 text-brand-text-primary'
                            : 'border-brand-border text-brand-text-secondary hover:border-brand-orange/60',
                          disabled || locked || committing ? 'cursor-not-allowed opacity-55' : '',
                        ].join(' ')}
                      >
                        <input
                          type="checkbox"
                          value={option.code}
                          checked={checked}
                          disabled={disabled || locked || committing}
                          data-reason-checkbox="true"
                          onChange={() => {
                            const nextReasons = checked
                              ? selectedReasonCodes.filter((code) => code !== option.code)
                              : [...selectedReasonCodes, option.code];
                            if (onReasonsChange) {
                              onReasonsChange(segment.segment_index, nextReasons);
                            } else if (!checked) {
                              onReasonChange(segment.segment_index, option.code);
                            }
                          }}
                          className="mt-0.5 h-4 w-4 accent-brand-orange"
                        />
                        <span className="min-w-0 leading-5">{displayReasonLabel(option)}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>

          {selectedReasonCodes.includes(OTHER_REASON_CODE) ? (
            <label className="mt-5 block text-xs font-medium text-brand-text-secondary">
              Other details
              <textarea
                value={selectedReasonNote}
                disabled={locked || committing}
                onChange={(event) =>
                  onReasonNoteChange(
                    segment.segment_index,
                    truncateUnicodeCodePoints(event.target.value)
                  )
                }
                rows={4}
                aria-invalid={currentOtherIncomplete}
                className="mt-2 w-full resize-y rounded-md border border-brand-border bg-brand-dark px-3 py-2 text-sm text-brand-text-primary outline-none focus:border-brand-orange aria-invalid:border-red-400 disabled:cursor-not-allowed disabled:opacity-55"
              />
              <span className="mt-1 flex items-center justify-between gap-3 text-xs">
                <span
                  className={currentOtherIncomplete ? 'text-red-300' : 'text-brand-text-secondary'}
                >
                  {currentOtherIncomplete ? 'Enter letters, numbers, or symbols.' : ' '}
                </span>
                <span className="font-mono text-brand-text-secondary">{noteLength} / 200</span>
              </span>
            </label>
          ) : null}

          <div className="mt-6 flex items-center justify-between text-xs text-brand-text-secondary">
            {segments.length - completed > 0 && nextIncompleteIndex != null ? (
              <button
                type="button"
                onClick={() => void onSegmentChange(nextIncompleteIndex)}
                title="Next incomplete Segment (N)"
                className="text-brand-orange underline decoration-brand-orange/50 underline-offset-2 hover:text-brand-orange-hover"
              >
                {segments.length - completed} incomplete · go to next
              </button>
            ) : (
              <span>All segments complete</span>
            )}
            <span className="font-mono">
              {completed} / {segments.length}
            </span>
          </div>

          <button
            type="button"
            onClick={onCommit}
            disabled={!complete || locked || committing}
            className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-orange px-4 text-sm font-semibold text-white transition hover:bg-brand-orange-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            {committing ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {commitStage === 'saving'
              ? 'Saving drafts'
              : commitStage === 'committing'
                ? 'Submitting complete episode'
                : commitStage === 'claiming'
                  ? 'Claiming next task'
                  : 'Commit & Next task'}
          </button>
        </aside>
      </div>
    </main>
  );
}
